import { NewVersionEnum } from '@/enums/newVersionRoute';
import { RouterV2Enum } from '@/router/router-names';
import router from '@/router';
import { agreementService } from '@/service/confirm-agreements';
import { identityScoreService } from '@/service/confirm-identity-score';
import { platformIdentityManager } from '@/shared/identity-verification';
import { AuthSceneTypeEnum } from '@/shared/identity-verification/types';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { ubtSendError } from '@/shared/utils/ubt';
import jumpToVerifyCredit, { checkVerifyCreditCompleteByScene } from '@/shared/verify-credit-controller';
import { OpenVerifyPageMode, VerifySceneEnum } from '@/shared/verify-credit-controller/types';
import store from '@/store';
import { dispatchActions } from '@/store/dispatchActions';
import { NormalPreCreditModelEnums } from '@/enums/home-type';
import { creditTaskSuspendSceneEnum } from '@/enums/autarkyUpgrade';
import PreApplyChooseAPI from '@/api/preApplyChoose';
import { livenessSignAgreementsPop } from '@/components/liveness-sign-agreements-pop';
import { convergeAgreementService } from '@/service/confirm-converge-agreements';

/** 进入授信流程的前置检查 */
async function creditPreCheck() {
  const { ocrAfterLoan } = store.state.base.businessInfoExtra?.userInfo || {};
  const syncUserInfoCachePromise = () => store.state.base.syncUserInfoCache
    || Promise.resolve({ hasNameAndIdCard: false });

  /** 是否命中ocr后置提现流程 */
  if (ocrAfterLoan) {
    const { hasNameAndIdCard } = await syncUserInfoCachePromise();
    /**
     * 有二要素用户，检验活体、授信...协议是否同意（无二要素用户，后置到二要素提交时检验协议）
     */
    if (hasNameAndIdCard) {
      /** 静默检查活体 */
      const hasLiveness = await PreApplyChooseAPI.silentCheckocr();

      if (hasLiveness) {
        /**
         * 检查是否有授信任务进行中
         * 注意：如果有授信任务进行中，说明协议已经签署过了，不需要再次检查
         */
        const progressingTaskId = store.state.home.creditData?.progressingTaskId;

        if (!progressingTaskId) {
          /** check同意协议(聚合后的协议：活体协议、授信协议、注册协议、征信协议) */
          const hasAgreemented = await convergeAgreementService.confirmConvergeAgreements();
          /** 若不同意协议，则中断流程 */
          if (!hasAgreemented) return Promise.reject();
        }
      } else {
        try {
          /** 3天内无活体记录，进入活体协议签署 & 活体认证 */
          await livenessSignAgreementsPop.open({ ocrAfterLoan: true, hasNameAndIdCard: true });
        } catch (err) {
          return Promise.reject();
        }
      }
    }
  } else {
    /** check是否同意必要的协议 */
    if ((await agreementService.confirmFinanceAgreements()) !== true) {
      return Promise.reject();
    }
  }

  /** 实名一致性check */
  try {
    await platformIdentityManager.checkUserAuth(
      AuthSceneTypeEnum.PRE_LENDING,
    );
  } catch (error) {
    return Promise.reject();
  }

  /** 身份证清晰度check */
  if (!(await identityScoreService.confirmIdentityScoreClarity())) {
    return Promise.reject();
  }

  return Promise.resolve(true);
}

/** 进入提现流程的前置检查 */
async function applyPreCheck() {
  /** 是否同意协议check */
  if (!(await agreementService.confirmFinanceAgreements())) {
    return Promise.reject();
  }

  return Promise.resolve(true);
}

/**
 * 预授信-进入提现流程前置是否实名检验
 * @param isRouterJump boolean 首页-点击“去提现”: true，提现-进入绑卡: false
 */
async function preCreditUserApplyPreCheck(
  isRouterJump = false,
  normalPreCreditModel?: NormalPreCreditModelEnums,
): Promise<boolean> {
  /** 零用钱提现流程，使用零用钱第一阶段场景，其他场景使用预授信第一阶段场景 */
  const targetScene = normalPreCreditModel === NormalPreCreditModelEnums.POCKET_MONEY_LOAN
    ? VerifySceneEnum.POCKET_MONEY_FIRST_INFO
    : VerifySceneEnum.PRE_CREDIT_FIRST_INFO;
  const verified = await checkVerifyCreditCompleteByScene(
    targetScene,
  );

  if (!verified) {
    return new Promise((resolve, reject) => {
      jumpToVerifyCredit(
        targetScene,
        {
          openMode: isRouterJump ? OpenVerifyPageMode.ROUTER_JUMP : OpenVerifyPageMode.POPUP_SHOW,
          successCb: () => resolve(isRouterJump),
          failCb: () => reject(),
        },
      );
    });
  }

  return Promise.resolve(false);
}

export interface UseDispatchProcessReturn {
  creditProcessDispatch: (useReplace?: boolean) => Promise<void>;
  applyProcessDispatch: (query?: Dictionary<string>) => Promise<void>;
  repayProcessDispatch: () => void;
}

/** 授信、提现、还款流程分发 */
function useDispatchProcess(): UseDispatchProcessReturn {
  /** 授信流程分发 */
  const creditProcessDispatch = async (useReplace = false) => {
    try {
      await Promise.all([
        /** 授信前置检查：是否同意协议、实名一致性、身份证清晰度 */
        creditPreCheck(),
        /** 后续用户状态获取: 授信状态、资料填写进度 */
        dispatchActions.userCreditStatus.getUserCredit(),
        /** 后续用户状态获取: 资料填写进度 */
        dispatchActions.userProfileProgress.getUserInfoProgress(),
      ]);

      const {
        /** 是否有授信中任务 */
        isProgressingTask,
        /** 是否有授信中任务 && 任务挂起 */
        isHangAndProgressingTask,
        /** 授信状态 */
        userCredit,
      } = store.state.userCreditStatus;
      const {
        /** 所有资料是否填写完成 */
        isAllProfileHasComplete,
        /** 第一阶段资料是否填写完成 */
        isPhaseOneProfileHasComplete,
      } = store.state.userProfileProgress;
      /** 授信中任务id */
      const progressingTaskId = userCredit?.progressingTaskId || '';
      const { ocrAfterLoan } = store.state.base.businessInfoExtra?.userInfo || {};

      if (isProgressingTask && (isHangAndProgressingTask || !isAllProfileHasComplete)) {
        /** 有进行中授信任务id && (任务挂起 or 资料不完整)，则跳到 资料补充页面 */
        jumpToVerifyCredit(
          userCredit?.suspendScene === creditTaskSuspendSceneEnum.AUTARKY_OCR_SUSPEND
            ? VerifySceneEnum.CREDIT_MIDDLE_ONLY_OCR
            : VerifySceneEnum.CREDIT_POST_SUPPLEMENT,
          {
            creditId: progressingTaskId,
          },
        );
      } else if (isProgressingTask && isAllProfileHasComplete) {
        /** 有进行中授信任务id && 资料已填完，则跳转到 授信结果页 */
        jumpToNewVersion(NewVersionEnum.CREDIT_RESULT, useReplace, `taskId=${progressingTaskId}`);
      } else if (isPhaseOneProfileHasComplete) {
        /** 若已完成授信阶段的资料（第一、二步），则跳转到 流量分发页 */
        router.app.$router[useReplace ? 'replace' : 'push']({
          path: RouterV2Enum.preApplyChoose,
        });
      } else {
        /** 无进行中授信任务id && 授信阶段资料未填写完，则跳转至 第一阶段资料认证页 */
        jumpToVerifyCredit(
          ocrAfterLoan
            ? VerifySceneEnum.CREDIT_PRE_VERIFY_OCR_AFTER : VerifySceneEnum.CREDIT_PRE_VERIFY,
        );
      }
    } catch (err) {
      ubtSendError(err as Error);
    }
  };

  /** 提现流程分发 */
  const applyProcessDispatch = async (query?: Dictionary<string>) => {
    try {
      await Promise.all([
        /** 授信前置检查：是否同意协议、实名一致性、身份证清晰度 */
        applyPreCheck(),
        /** 后续用户状态获取: 授信状态、资料填写进度 */
        dispatchActions.userCreditStatus.getUserCredit(),
      ]);

      /** 预授信，前置检验是否OCR上传 */
      if (store.state.userCreditStatus.userCredit?.preCredit) {
        const addedHistory = await preCreditUserApplyPreCheck(
          true,
          store.state.userCreditStatus.userCredit?.normalPreCreditModel,
        );

        /** 若是有资料填写路由记录，进入提现页前需使用replace替换记录，避免back返回时跳回Verify页面 */
        router.app.$router[addedHistory ? 'replace' : 'push']({
          path: RouterV2Enum.applyProduct,
          query,
        });
      } else {
        /** 非预授信 */
        router.app.$router.push({
          path: RouterV2Enum.applyProduct,
          query,
        });
      }
    } catch (err) {
      ubtSendError(err as Error);
    }
  };

  /** 还款流程分发 */
  const repayProcessDispatch = () => {
    const jumpToRepayBills = () => {
      jumpToNewVersion(NewVersionEnum.RepaymentBills, false);
    };

    if (store.state.home.creditData?.planDetail) {
      const {
        loanOrderId, commodityId, capitalCode = '', period,
      } = store.state.home.creditData?.planDetail;

      if (loanOrderId && commodityId) {
        jumpToNewVersion(
          NewVersionEnum.Repay,
          false,
          `loanOrderId=${loanOrderId}&commodityId=${commodityId}&capitalCode=${capitalCode}&period=${period}&showRepaymentPlan=true`,
        );
        return;
      }
    }

    /** 无逾期账单、服务异常时，跳转到还款账单页 */
    jumpToRepayBills();
  };

  return {
    creditProcessDispatch,
    applyProcessDispatch,
    repayProcessDispatch,
  };
}

export default useDispatchProcess;
